import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircleNotch,
  faExclamationTriangle,
  faFileAlt,
  faMinus,
  faSearch,
  faStopwatch,
  faTools,
} from '@fortawesome/free-solid-svg-icons';
import { Progress, UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import css from 'styled-jsx/css';

import SchedulesService from '../../Services/SchedulesService';
import ReminderMessagePreview from '../Maintenance/ReminderMessagePreviewDisplay';
import { MAINTENANCE_LOG_ID } from '../../Constants/StaticUUIDs';

export default memo(AssetMaintenanceTable);

const selectOptions = [
  { label: 'Complete', value: 'complete' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Overdue', value: 'due' },
];

const selectStyles = {
  control: (base) => ({ ...base, border: 'none', boxShadow: 'none' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  option: (base) => ({
    ...base,
    backgroundColor: 'var(--white) !important',
    outline: 'none',
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-light-gray)',
    fontWeight: 'normal',
  }),
};

const iconDisplayMap = new Map([
  ['upcoming', { icon: faExclamationTriangle, color: 'warning-yellow' }],
  ['due', { icon: faStopwatch, color: 'active-dm' }],
  ['complete', { icon: faCheckCircle, color: 'alert-confirmation' }],
]);

const frequencyDisplayTextMap = new Map([
  ['weekly', 'week'],
  ['monthly', 'month'],
  ['yearly', 'year'],
]);

const initState = {
  error: '',
  partsQuoteRequestPending: '',
  serviceManualRequestPending: '',
};

function AssetMaintenanceTable(props) {
  const source = CancelToken.source();
  const [state, updateState] = useState(initState);
  const [messageModalState, updateMessageModalState] = useState({
    instance: {},
    show: false,
  });
  const service = new SchedulesService();

  const downloadPartsQuote = async (
    { id, orgId: orgID, reminder: { id: reminderId } = {}, scheduleId },
    i
  ) => {
    try {
      updateState({ ...state, partsQuoteRequestPending: `${id}-${i}` });
      const { attachment } = await service.getReminderAttachment({
        scheduleId,
        reminderId,
        orgID,
        cancelToken: source.token,
      });
      updateState({ ...state, partsQuoteRequestPending: '' });
      window.open(attachment, '_blank');
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, partsQuoteRequestPending: '' });
    }
  };

  const downloadServiceManual = async ({ id, orgId: orgID, scheduleId }, i) => {
    try {
      updateState({ ...state, serviceManualRequestPending: `${id}-${i}` });
      const { serviceManual } = await service.getServiceManual({
        scheduleId,
        orgID,
        cancelToken: source.token,
      });
      updateState({ ...state, serviceManualRequestPending: '' });
      window.open(serviceManual, '_blank');
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, serviceManualRequestPending: '' });
    }
  };

  useEffect(() => source.cancel, []);

  return (
    <>
      <table className="d-none d-lg-table table">
        <thead>
          <tr className="text-left">
            <th
              className="sticky-top bg-white border-0 align-middle text-center"
              style={{ width: '3%' }}
            >
              <div className="form-check">
                <input
                  checked={props.allInstancesChecked}
                  className="form-check-input mt-n2"
                  onChange={props.handleAllRowsChecked}
                  type="checkbox"
                />
              </div>
            </th>
            <th
              className="sticky-top bg-white border-0 align-middle px-0"
              style={{ width: '10%' }}
            >
              <Select
                closeMenuOnSelect={false}
                controlShouldRenderValue={false}
                formatOptionLabel={(opt) =>
                  formatOptionLabel(opt, props.typeFilter)
                }
                isClearable={false}
                isOptionSelected={() => false}
                isSearchable={false}
                onChange={props.handleTypeFilterSelect}
                options={selectOptions}
                placeholder="Status"
                styles={selectStyles}
              />
            </th>
            <th
              className="sticky-top bg-white border-0"
              style={{ width: '18%' }}
            >
              <div className="input-group">
                <input
                  className="form-control border-0 text-text-light-gray"
                  name="scheduleNameSearch"
                  onChange={props.handlePropUpdate}
                  placeholder="Schedule"
                  type="text"
                  value={props.scheduleNameSearch}
                />
                <div className="input-group-append">
                  <div className="input-group-text bg-white border-0">
                    <FontAwesomeIcon
                      className="text-text-light-gray"
                      icon={faSearch}
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            </th>
            <th
              className="sticky-top bg-white border-0"
              style={{ width: '32%' }}
            >
              <div className="input-group">
                <input
                  className="form-control border-0 text-text-light-gray"
                  name="reminderNameSearch"
                  onChange={props.handlePropUpdate}
                  placeholder="Reminder"
                  type="text"
                  value={props.reminderNameSearch}
                />
                <div className="input-group-append">
                  <div className="input-group-text bg-white border-0">
                    <FontAwesomeIcon
                      className="text-text-light-gray"
                      icon={faSearch}
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            </th>
            <th
              className="sticky-top bg-white border-0 align-middle text-text-light-gray font-weight-normal"
              style={{ width: '12%' }}
            >
              Date Trigger
            </th>
            <th
              className="sticky-top bg-white border-0 align-middle text-text-light-gray font-weight-normal"
              style={{ width: '12%' }}
            >
              Date Service
            </th>
            <th
              className="sticky-top bg-white border-0 align-middle text-text-light-gray font-weight-normal px-0 text-nowrap"
              style={{ width: '5%' }}
            >
              Parts Revenue
            </th>

            <th
              className="sticky-top bg-white border-0"
              style={{ width: '8%' }}
            ></th>
            <th
              className="sticky-top bg-white border-0"
              style={{ width: '5%' }}
            ></th>
            <th
              className="sticky-top bg-white border-0"
              style={{ width: '5%' }}
            ></th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map(([key, instance], i) => {
            const instanceType = parseInstanceType(instance);
            const { icon, color } = iconDisplayMap.get(instanceType) || {};
            const partsQuote = instance?.reminder?.attachment ?? '';
            return (
              <tr className="h-100 py-2" key={key}>
                <td className="align-middle text-center">
                  {instanceType === 'complete' ? null : (
                    <div className="form-check">
                      <input
                        checked={props.checkedInstances.has(key)}
                        className="form-check-input mt-n2"
                        onChange={() =>
                          props.handleInstanceChecked([key, instance])
                        }
                        type="checkbox"
                      />
                    </div>
                  )}
                </td>
                <td className="align-middle text-center">
                  <FontAwesomeIcon
                    className={`text-${color}`}
                    icon={icon}
                    size="lg"
                  />
                </td>
                <td className="align-middle">
                  <Link
                    className="btn btn-link nine-tenth-text text-left"
                    to={`/schedule/${instance.scheduleId}`}
                  >
                    {instance.scheduleName || ''}
                  </Link>
                </td>
                <td className="p-0 h-100 position-relative">
                  <ReminderCell
                    {...instance}
                    assetShadowSignalsMap={props.assetShadowSignalsMap}
                    color={color}
                    instanceType={instanceType}
                  />
                </td>
                <td className="align-middle">
                  <span className="text-text-light-gray">
                    {moment(instance.createdAt).format('MMMM Do, Y')}
                  </span>
                </td>
                <td className="align-middle">
                  <span className="text-text-light-gray">
                    {instance.serviceDate
                      ? moment(instance.serviceDate).format('MMMM Do, Y')
                      : ''}
                  </span>
                </td>
                <td className="align-middle">
                  <p className="mb-0 text-alert-confirmation text-center">
                    {instance.partsCost ? (
                      `${instance.partsCost}`
                    ) : (
                      <FontAwesomeIcon
                        className="text-alert-confirmation"
                        icon={faMinus}
                        size="lg"
                      />
                    )}
                  </p>
                </td>
                <td className="align-middle">
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-lg three-quarter-text text-nowrap"
                    onClick={() =>
                      updateMessageModalState({ show: true, instance })
                    }
                  >
                    View Message
                  </button>
                </td>
                <td className="align-middle">
                  <button
                    className="btn btn-link p-0 text-decoration-none shadow-none"
                    disabled={
                      state.partsQuoteRequestPending ||
                      state.serviceManualRequestPending
                    }
                    id={`instance-quote-${i}`}
                    onClick={() =>
                      partsQuote && downloadPartsQuote(instance, i)
                    }
                  >
                    <FontAwesomeIcon
                      className={partsQuote ? '' : 'text-muted'}
                      icon={
                        state.partsQuoteRequestPending === `${instance.id}-${i}`
                          ? faCircleNotch
                          : faFileAlt
                      }
                      size="lg"
                      spin={
                        state.partsQuoteRequestPending === `${instance.id}-${i}`
                      }
                    />
                  </button>
                  <UncontrolledTooltip
                    innerClassName="three-quarter-text rounded-pill"
                    target={`instance-quote-${i}`}
                  >
                    {partsQuote ? 'Download Parts Quote' : 'No parts quote'}
                  </UncontrolledTooltip>
                </td>
                <td className="align-middle">
                  <button
                    className="btn btn-link p-0 text-decoration-none shadow-none"
                    disabled={
                      state.partsQuoteRequestPending ||
                      state.serviceManualRequestPending
                    }
                    id={`instance-manual-${i}`}
                    onClick={() =>
                      instance.serviceManual &&
                      downloadServiceManual(instance, i)
                    }
                  >
                    <FontAwesomeIcon
                      className={instance.serviceManual ? '' : 'text-muted'}
                      icon={
                        state.serviceManualRequestPending ===
                        `${instance.id}-${i}`
                          ? faCircleNotch
                          : faTools
                      }
                      size="lg"
                      spin={
                        state.serviceManualRequestPending ===
                        `${instance.id}-${i}`
                      }
                    />
                  </button>
                  <UncontrolledTooltip
                    innerClassName="three-quarter-text rounded-pill"
                    target={`instance-manual-${i}`}
                  >
                    {instance.serviceManual
                      ? 'Download Maintenance Manual'
                      : 'No Service Manual'}
                  </UncontrolledTooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {messageModalState.show ? (
        <ReminderMessagePreview
          instance={messageModalState.instance}
          isOpen={messageModalState.show}
          message={messageModalState.instance?.reminder?.message}
          toggleModal={() =>
            updateMessageModalState({ show: false, instance: {} })
          }
        />
      ) : null}
    </>
  );
}

AssetMaintenanceTable.propTypes = {
  allInstancesChecked: PropTypes.bool,
  assetShadowSignalsMap: PropTypes.object,
  checkedInstances: PropTypes.object,
  handleAllRowsChecked: PropTypes.func,
  handleInstanceChecked: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  handleTypeFilterSelect: PropTypes.func,
  reminderNameSearch: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.array),
  scheduleNameSearch: PropTypes.string,
  typeFilter: PropTypes.array,
};

AssetMaintenanceTable.defaultProps = { rows: [] };

function formatOptionLabel({ label, value }, selected) {
  const isSelected = selected.includes(value);
  return (
    <div className="input-group input-group-sm">
      <div className="input-group-prepend">
        <div className="input-group-text bg-white border-0">
          <input checked={isSelected} readOnly type="checkbox" />
        </div>
      </div>
      <input
        className="form-control border-0 bg-white px-0"
        readOnly
        type="text"
        value={label}
      />
    </div>
  );
}

formatOptionLabel.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
};

const { className: progressClass, styles: progressStyles } = css.resolve`
  div.progress {
    height: 0.5rem;
  }
`;

function ReminderCell(props) {
  const {
    assetShadowSignalsMap,
    color,
    dueOn,
    instanceType,
    reminder: {
      name: reminderName,
      recurring,
      trigger: { frequency, interval, name: signalName, type } = {},
    } = {},
    value,
  } = props;
  const { value: signalValue = 0 } =
    assetShadowSignalsMap.get(signalName) ?? {};
  const roundedValue = Math.round((signalValue + Number.EPSILON) * 100) / 100;
  const overdueDisplayValue =
    Math.round((signalValue - value + Number.EPSILON) * 100) / 100;
  const upcomingDisplayValue =
    Math.round((value - signalValue + Number.EPSILON) * 100) / 100;

  const now = moment();
  const due = moment(dueOn);
  const timeDiff = now.diff(due, 'days');
  const timeDiffDisplayText =
    instanceType === 'due' && timeDiff ? 'Days Overdue' : 'Days Left';
  const recurringDisplayText = recurring ? 'Every' : 'Once at';
  const frequencyDisplayText = frequencyDisplayTextMap.get(frequency);

  return type === 'conditional' ? (
    <div className="position-absolute h-100 w-100 py-2 px-4 d-flex flex-column justify-content-center">
      <p className="mb-0 three-quarter-text text-dark">{reminderName ?? ''}</p>
      {instanceType === 'complete' ? (
        <p className={`mt-2 mb-0 text-${color}`}>Service Completed</p>
      ) : (
        <Progress
          className={`w-100 my-1 ${progressClass}`}
          color={color}
          value={instanceType === 'due' ? 100 : (signalValue / value) * 100}
        />
      )}
      <span
        className={
          instanceType === 'complete'
            ? 'd-none'
            : 'w-100 d-flex justify-content-between'
        }
      >
        <span className={`text-${color} three-quarter-text`}>
          {instanceType === 'due'
            ? `${overdueDisplayValue} hours overdue`
            : `${upcomingDisplayValue} hours remaining`}
        </span>
        <span
          className={
            instanceType === 'upcoming'
              ? 'd-none'
              : `text-${color} three-quarter-text`
          }
        >
          {roundedValue} Total Hours
        </span>
      </span>
      {progressStyles}
    </div>
  ) : (
    <div className="h-100 w-100 position-absolute row no-gutters px-4 py-2">
      <div className="col-8 py-1 pr-1">
        <p className="mb-1 nine-tenth-text text-dark text-truncate">
          {reminderName ?? ''}
        </p>
        <p className="mb-0 three-quarter-text text-text-light-gray">
          {`${recurringDisplayText ?? ''} ${interval > 1 ? interval : ''} ${
            interval > 1 ? `${frequencyDisplayText}s` : frequencyDisplayText
          }`}
        </p>
      </div>
      <div className="col-4">
        <div
          className={`h-100 rounded-lg border border-${color} d-flex flex-column align-items-center justify-content-around`}
        >
          {instanceType === 'complete' ? (
            <p className={`text-${color} three-quarter-text mb-0 text-center`}>
              Service Complete
            </p>
          ) : (
            <>
              <p className={`mb-0 text-${color}`}>{Math.abs(timeDiff)}</p>
              <p className="three-quarter-text mb-0 text-text-light-gray text-center text-nowrap">
                {timeDiffDisplayText}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

ReminderCell.propTypes = {
  assetShadowSignalsMap: PropTypes.object,
  color: PropTypes.oneOf(['active-dm', 'alert-confirmation', 'warning-yellow']),
  dueOn: PropTypes.string,
  instanceType: PropTypes.oneOf(['complete', 'due', 'upcoming']),
  reminder: PropTypes.shape({
    name: PropTypes.string,
    recurring: PropTypes.bool,
    trigger: PropTypes.shape({
      frequency: PropTypes.oneOf(['weekly', 'monthly', 'yearly']),
      interval: PropTypes.number,
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  }),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

//
function parseInstanceType(instance) {
  const { due, maintenanceLogId } = instance;

  switch (true) {
    case !!maintenanceLogId && maintenanceLogId !== MAINTENANCE_LOG_ID:
      return 'complete';
    case !due:
      return 'upcoming';
    case due && (!maintenanceLogId || maintenanceLogId === MAINTENANCE_LOG_ID):
      return 'due';
  }
}
